import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDailyClipQuery } from '../../hooks/use-daily';
import { ClipAnswerPanel } from '../clips/ClipAnswerPanel';
import { Skeleton } from '../shared/Skeleton';
import { Icon } from '../shared/Icons';

// The design's centrepiece — today's assigned clip, answered INLINE right here. The daily loop (M9)
// assigns one scenario per referee per day, and GET /me/daily returns the full playback payload, so
// the referee watches, decides, and sees the reveal without leaving Home — the submitted attempt is
// what advances the streak. Discussion is hidden on this compact card (it lives on the full clip
// page, linked below). `enabled` gates the query to clip-answering roles.

interface TodaysClipHeroProps {
  enabled: boolean;
}

export function TodaysClipHero({ enabled }: TodaysClipHeroProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useDailyClipQuery(enabled);

  if (isLoading) return <HeroSkeleton />;

  if (isError) {
    // Data-fetch failures render inline (never a toast) — this is where the clip would be.
    return (
      <section className="flex flex-col rounded border border-line bg-surface p-5 shadow-1">
        <Eyebrow />
        <div className="mt-4 flex flex-col items-center justify-center rounded-sm border border-line-2 bg-bg-2 py-10 text-center">
          <p className="text-[13px] text-muted">{t('home.todaysClip.couldNotLoad')}</p>
          <p className="mt-1 text-xs text-muted-2">{t('home.todaysClip.refreshToRetry')}</p>
        </div>
      </section>
    );
  }

  if (!data?.clip) return <EmptyHero />;

  return (
    <div className="space-y-3">
      {/* The full watch → decide → reveal flow, inline. showDiscussion is off — the compact Home card
          keeps to the decision; the thread lives on the full clip page (linked below). */}
      <ClipAnswerPanel
        clip={data.clip}
        isDaily
        showDiscussion={false}
        onSourceExpired={() => refetch()}
      />

      {/* Continue on the full clip page — where the discussion thread unlocks after answering. */}
      <div className="flex justify-center">
        <Link
          to={`/clips?clip=${data.clip.id}`}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-accent hover:text-accent-2"
        >
          {data.answered ? t('home.todaysClip.openFullAnswered') : t('home.todaysClip.openFullUnanswered')}
          <Icon.arrow className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}

// Shared eyebrow label for the non-clip states (loading/error/empty). The loaded state uses
// ClipAnswerPanel's own "Daily clip" eyebrow instead.
function Eyebrow() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-accent">
      <Icon.spark className="h-3.5 w-3.5" />
      {t('home.todaysClip.eyebrow')}
    </div>
  );
}

// The referee's federation has no published clips in their sport yet — honest empty state with the
// real fallback action (the library, which will also be empty but is the right place to land).
function EmptyHero() {
  const { t } = useTranslation();
  return (
    <section className="flex flex-col rounded border border-line bg-surface p-5 shadow-1">
      <Eyebrow />
      <h2 className="mt-3 font-display text-2xl leading-snug text-ink">{t('home.todaysClip.noClipToday')}</h2>
      <p className="mt-1.5 max-w-md text-[13px] leading-relaxed text-muted">{t('home.todaysClip.noClipBody')}</p>
      <div className="relative mt-4 grid aspect-video w-full flex-none place-items-center overflow-hidden rounded-sm border border-line-2 bg-bg-2">
        <span className="grid h-12 w-12 place-items-center rounded-full bg-surface text-muted shadow-1">
          <Icon.clip className="h-5 w-5" />
        </span>
      </div>
      <div className="mt-auto pt-5">
        <Link
          to="/clips"
          className="inline-flex h-10 items-center gap-2 rounded-btn bg-accent px-4 text-sm font-semibold text-accent-ink transition-colors hover:bg-accent-2"
        >
          {t('home.todaysClip.browseLibrary')}
          <Icon.arrow className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}

function HeroSkeleton() {
  return (
    <section className="flex flex-col rounded border border-line bg-surface p-5 shadow-1">
      <Eyebrow />
      <Skeleton className="mt-3 h-7 w-3/4" />
      <Skeleton className="mt-4 aspect-video w-full flex-none" />
      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Skeleton className="h-11 w-full" />
        <Skeleton className="h-11 w-full" />
      </div>
      <div className="mt-3">
        <Skeleton className="h-10 w-36" />
      </div>
    </section>
  );
}
